import { useCanManageCareerPreferencesBool, useRequireCapability } from "@/hooks/useCapabilities";
import { PERMISSION, DOMAIN } from "@/hooks/useCapabilities";

import SectionHeading from "@/components/SectionHeading";
import { useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_JOB_PREFERENCES_QUERY } from "./queries";
import { useParams } from "react-router-dom";
import JobPreferencesSkeleton from "@/pages/_layouts/jobs/_components/JobPreferencesSkeleton";
import EmptyState from "@/components/EmptyState";
import EditableJobPreferences from "./_components/EditableJobPreferences";
import CreateJobPreferences from "./_components/CreateJobPreferences";
import DeleteJobPreferencesModal from "./_components/DeleteJobPreferencesModal";

export interface JobPreferencesFormData {
  jobSearchStatus: string;
  optedInToJobSeekerEmails: boolean;
  jobCompensationMinimum: string;
  openToSeasonJobs: boolean;
  locationPreferences: string[];
  interestInInternationalPositions: boolean;
  jobCategories: string[];
}

export default function JobPreferences() {
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewCareerPreferences });
  const { pgaId } = useParams();
  const [creatingJobPreferences, setCreatingJobPreferences] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const canManageCareerPreferences = useCanManageCareerPreferencesBool();

  const {
    data,
    loading,
    error: getJobPreferencesError,
    refetch
  } = useQuery(GET_JOB_PREFERENCES_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first"
  });

  if (getJobPreferencesError) {
    return <div>Error: {getJobPreferencesError.message}</div>;
  }

  if (loading) {
    return <JobPreferencesSkeleton />;
  }

  const noJobPreferences = !data.person.jobPreferences;

  const renderState = () => {
    if (noJobPreferences && !creatingJobPreferences) {
      return (
        <EmptyState
          title="No Job Preferences"
          iconName="screenshot_keyboard"
          caption="No Job Preferences have been created for this person."
          {...(canManageCareerPreferences
            ? {
                action: {
                  children: "Create Job Preferences",
                  onClick: () => setCreatingJobPreferences(true),
                  icon: "add"
                }
              }
            : {})}
        />
      );
    }

    if (noJobPreferences && creatingJobPreferences) {
      return (
        <CreateJobPreferences setCreatingJobPreferences={setCreatingJobPreferences} refetchPreferences={refetch} />
      );
    }

    return <EditableJobPreferences jobPreferencesData={data} />;
  };

  return (
    <>
      <SectionHeading
        heading="Job Preferences"
        body="View and manage job preferences"
        {...(canManageCareerPreferences && data?.person?.jobPreferences
          ? {
              actions: [
                {
                  label: "Delete Job Preferences",
                  onClick: () => setDeleteModalOpen(true),
                  icon: "delete",
                  variant: "text"
                }
              ]
            }
          : {})}
      />
      {renderState()}
      <DeleteJobPreferencesModal
        open={deleteModalOpen}
        setOpen={(e) => setDeleteModalOpen(e)}
        jobPreferencesId={data?.person?.jobPreferences?.id}
        refetchPreferences={refetch}
      />
    </>
  );
}
